// Section 22 People tab: a sortable table of everyone (name, age, sex, job, strength, health,
// happiness, trust, top skills, household, flags). Above 60 people it groups by household or
// job with expand (Section 3). Clicking a name opens the person card; hovering highlights the
// person across panels.
import type { PersonId } from '../../sim/api';
import type { PeopleGrouping, PeopleSortKey } from '../../store';
import type { PeopleTableVM, PersonRow } from '../../viewModels';
import { jobLabel } from '../../viewModels';
import { oneDecimal, whole } from '../format';
import { uiText } from '../text/en';
import { Glyphs } from '../shell/glyphs';

interface Props {
  vm: PeopleTableVM;
  sortKey: PeopleSortKey;
  sortDir: 'asc' | 'desc';
  grouping: PeopleGrouping;
  hovered: PersonId | null;
  onSort: (key: PeopleSortKey) => void;
  onGrouping: (g: PeopleGrouping) => void;
  onToggleGroup: (key: string) => void;
  onHover: (id: PersonId | null) => void;
  onOpen: (id: PersonId) => void;
}

const COLUMNS: [PeopleSortKey, string][] = [
  ['name', uiText.people.name],
  ['age', uiText.people.age],
  ['sex', uiText.people.sex],
  ['job', uiText.people.job],
  ['strength', uiText.people.strength],
  ['health', uiText.people.health],
  ['happiness', uiText.people.happiness],
  ['trust', uiText.people.trust],
  ['skill', uiText.people.skills],
  ['household', uiText.people.household],
];

function Row({ r, hovered, onHover, onOpen }: { r: PersonRow } & Pick<Props, 'hovered' | 'onHover' | 'onOpen'>) {
  const t = uiText.people;
  return (
    <tr
      class={r.id === hovered ? 'is-hovered' : ''}
      onMouseEnter={() => onHover(r.id)}
      onMouseLeave={() => onHover(null)}
    >
      <th scope="row" class="name">
        <button
          type="button"
          class="link"
          onClick={() => onOpen(r.id)}
          onFocus={() => onHover(r.id)}
          onBlur={() => onHover(null)}
        >
          {r.name}
        </button>
        {r.pendingName !== null && <span class="pending"> {t.renamed(r.pendingName)}</span>}
      </th>
      <td class="num">{r.ageYears}</td>
      <td>{uiText.sex[r.sex]}</td>
      <td>
        {r.jobLabel}
        {r.pending && <span class="pending"> {t.pending}</span>}
      </td>
      <td class="num">{whole(r.strength)}</td>
      <td class="num">{whole(r.health)}</td>
      <td class="num">{whole(r.happiness)}</td>
      <td class="num">{whole(r.trust)}</td>
      <td class="skills">
        {r.topSkills.slice(0, 2).map((s) => (
          <span key={s.skill} class="skills__item">
            {jobLabel(s.skill)} <span class="num">{oneDecimal(s.level)}</span>
          </span>
        ))}
      </td>
      <td>{r.householdName}</td>
      <td class="flags">
        <Glyphs flags={r.flags} words />
      </td>
    </tr>
  );
}

export function PeopleTable(props: Props) {
  const { vm, sortKey, sortDir, grouping, onSort, onGrouping, onToggleGroup } = props;
  const t = uiText.people;
  const colSpan = COLUMNS.length + 1;
  return (
    <div class="people">
      <div class="people__tools">
        <label class="field field--inline">
          <span class="field__label">{t.group}</span>
          <select value={grouping} onChange={(e) => onGrouping(e.currentTarget.value as PeopleGrouping)}>
            <option value="auto">{t.groupAuto}</option>
            <option value="none">{t.groupNone}</option>
            <option value="household">{t.groupHousehold}</option>
            <option value="job">{t.groupJob}</option>
          </select>
        </label>
      </div>
      <table class="table people__table">
        <caption class="visually-hidden">
          {t.caption}. {t.sortedBy(COLUMNS.find(([k]) => k === sortKey)?.[1] ?? sortKey, sortDir)}
        </caption>
        <thead>
          <tr>
            {COLUMNS.map(([key, label]) => (
              <th
                key={key}
                scope="col"
                aria-sort={key === sortKey ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                <button type="button" class="link sort" onClick={() => onSort(key)}>
                  {label}
                  <span aria-hidden="true" class="sort__mark">
                    {key === sortKey ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </span>
                </button>
              </th>
            ))}
            <th scope="col">{t.flags}</th>
          </tr>
        </thead>
        {vm.groupBy === 'none' ? (
          <tbody>
            {vm.rows.map((r) => (
              <Row key={r.id} r={r} {...props} />
            ))}
          </tbody>
        ) : (
          vm.groups.map((g) => (
            <tbody key={g.key} class="people__group">
              <tr class="people__group-head">
                <th scope="rowgroup" colSpan={colSpan}>
                  <button
                    type="button"
                    class="link"
                    aria-expanded={g.expanded}
                    onClick={() => onToggleGroup(g.key)}
                  >
                    <span aria-hidden="true">{g.expanded ? '▾ ' : '▸ '}</span>
                    {g.kind === 'household' ? uiText.list.household(g.label, g.rows.length) : g.label}
                  </button>{' '}
                  <span class="hint">
                    {t.groupSummary(g.rows.length, g.flagged)} · {t.strength}{' '}
                    <span class="num">{whole(g.meanStrength)}</span>
                  </span>
                </th>
              </tr>
              {g.expanded && g.rows.map((r) => <Row key={r.id} r={r} {...props} />)}
            </tbody>
          ))
        )}
      </table>
    </div>
  );
}
